import { useParams } from "@tanstack/solid-router";
import { For, Show, createSignal } from "solid-js";

import { AppShell } from "~/components/AppShell";
import {
  useArchivePipeline,
  useArchiveStage,
  useCreatePipeline,
  useCreateStage,
  useMakeDefaultPipeline,
  usePipelines,
  useStages,
  useUpdateStage,
} from "~/lib/hooks";
import type { PipelineRead, StageRead, StageType } from "~/lib/types";

export function PipelineAdminPage() {
  const params = useParams({ strict: false }) as () => { wsKey: string };

  const pipelines = usePipelines(() => params().wsKey);
  const createPipeline = useCreatePipeline(() => params().wsKey);
  const archivePipeline = useArchivePipeline(() => params().wsKey);
  const makeDefaultPipeline = useMakeDefaultPipeline(() => params().wsKey);

  const [showCreatePipe, setShowCreatePipe] = createSignal(false);
  const [createPipeError, setCreatePipeError] = createSignal<string | null>(null);
  const [selectedPipelineId, setSelectedPipelineId] = createSignal<string | null>(null);

  const visiblePipelines = () =>
    (pipelines.data ?? []).filter((p) => !p.archived_at);

  // Resolve the active selection: explicit pick → default → first.
  const activePipeline = (): PipelineRead | undefined => {
    const list = visiblePipelines();
    const explicit = list.find((p) => p.id === selectedPipelineId());
    return explicit ?? list.find((p) => p.is_default) ?? list[0];
  };

  const handleCreatePipeline = async (e: SubmitEvent) => {
    e.preventDefault();
    setCreatePipeError(null);
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const key = String(data.get("key") ?? "").trim();
    const name = String(data.get("name") ?? "").trim();
    if (!key || !name) return;
    try {
      const p = await createPipeline.mutateAsync({
        key,
        name,
        description: String(data.get("description") ?? "") || null,
      });
      setSelectedPipelineId(p.id);
      setShowCreatePipe(false);
      form.reset();
    } catch (err) {
      setCreatePipeError((err as Error).message);
    }
  };

  return (
    <AppShell wsKey={params().wsKey} pageTitle="Pipelines">
      <div class="flex flex-col gap-4">
        <Show when={pipelines.isLoading}>
          <div role="status" class="text-center py-8 opacity-60">
            Loading…
          </div>
        </Show>
        <Show when={pipelines.isError}>
          <div role="alert" class="alert alert-error">
            Failed to load pipelines.
          </div>
        </Show>

        <Show when={pipelines.data}>
          <div class="card bg-base-100 shadow-sm">
            <div class="card-body">
              <div class="flex items-center justify-between">
                <h2 class="card-title text-lg">Pipelines</h2>
                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  onClick={() => setShowCreatePipe(true)}
                >
                  New pipeline
                </button>
              </div>
              <Show when={visiblePipelines().length === 0}>
                <div class="text-center py-6 opacity-60 text-sm">
                  No pipelines yet. Create one to start tracking deals.
                </div>
              </Show>
              <ul class="divide-y divide-base-300 mt-2">
                <For each={visiblePipelines()}>
                  {(p) => (
                    <li
                      class={`py-2 flex items-center justify-between gap-2 ${
                        p.id === activePipeline()?.id ? "bg-base-200/60" : ""
                      } rounded-md px-2`}
                    >
                      <button
                        type="button"
                        class="text-left flex-1 min-w-0"
                        onClick={() => setSelectedPipelineId(p.id)}
                      >
                        <div class="font-medium flex items-center gap-2">
                          {p.name}
                          <Show when={p.is_default}>
                            <span class="badge badge-primary badge-xs">default</span>
                          </Show>
                        </div>
                        <div class="text-xs opacity-60 truncate">
                          key: <code>{p.key}</code>
                          <Show when={p.description}> · {p.description}</Show>
                        </div>
                      </button>
                      <div class="flex gap-1">
                        <Show when={!p.is_default}>
                          <button
                            type="button"
                            class="btn btn-ghost btn-xs"
                            disabled={makeDefaultPipeline.isPending}
                            onClick={() =>
                              makeDefaultPipeline.mutate({
                                pipelineId: p.id,
                                version: p.version,
                              })
                            }
                            title="Make this the default pipeline for new deals"
                          >
                            Make default
                          </button>
                        </Show>
                        <Show when={!p.is_default && visiblePipelines().length > 1}>
                          <button
                            type="button"
                            class="btn btn-ghost btn-xs text-error"
                            disabled={archivePipeline.isPending}
                            onClick={() =>
                              archivePipeline.mutate({
                                pipelineId: p.id,
                                version: p.version,
                              })
                            }
                            aria-label={`Archive pipeline ${p.name}`}
                          >
                            Archive
                          </button>
                        </Show>
                      </div>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </div>

          <Show when={activePipeline()}>
            {(p) => (
              <StagesPanel
                wsKey={() => params().wsKey}
                pipeline={p()}
              />
            )}
          </Show>
        </Show>
      </div>

      <Show when={showCreatePipe()}>
        <dialog open class="modal modal-open" aria-modal="true" aria-labelledby="new-pipe-title">
          <div class="modal-box">
            <h3 id="new-pipe-title" class="font-bold text-lg mb-3">
              New pipeline
            </h3>
            <form onSubmit={handleCreatePipeline} class="flex flex-col gap-3">
              <input
                name="key"
                class="input input-bordered"
                placeholder="key (e.g. enterprise-sales)"
                aria-label="Pipeline key"
                required
                pattern="[a-z0-9][a-z0-9_-]*"
                maxLength={63}
              />
              <input
                name="name"
                class="input input-bordered"
                placeholder="Display name"
                aria-label="Pipeline name"
                required
              />
              <input
                name="description"
                class="input input-bordered"
                placeholder="Description (optional)"
                aria-label="Pipeline description"
              />
              <Show when={createPipeError()}>
                <div role="alert" class="alert alert-error text-sm">
                  {createPipeError()}
                </div>
              </Show>
              <div class="modal-action">
                <button
                  type="button"
                  class="btn btn-ghost"
                  onClick={() => setShowCreatePipe(false)}
                >
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" disabled={createPipeline.isPending}>
                  {createPipeline.isPending ? "Saving…" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </dialog>
      </Show>
    </AppShell>
  );
}

function StagesPanel(props: { wsKey: () => string; pipeline: PipelineRead }) {
  const stages = useStages(props.wsKey, () => props.pipeline.id);
  const createStage = useCreateStage(props.wsKey);
  const updateStage = useUpdateStage(props.wsKey);
  const archiveStage = useArchiveStage(props.wsKey);

  const [showCreate, setShowCreate] = createSignal(false);
  const [createErr, setCreateErr] = createSignal<string | null>(null);

  const handleCreateStage = async (e: SubmitEvent) => {
    e.preventDefault();
    setCreateErr(null);
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const key = String(data.get("key") ?? "").trim();
    const name = String(data.get("name") ?? "").trim();
    if (!key || !name) return;
    const stageType = String(data.get("stage_type") ?? "open") as StageType;
    const probStr = String(data.get("probability_pct") ?? "").trim();
    try {
      await createStage.mutateAsync({
        pipelineId: props.pipeline.id,
        body: {
          key,
          name,
          stage_type: stageType,
          probability_pct: probStr ? Number(probStr) : undefined,
        },
      });
      setShowCreate(false);
      form.reset();
    } catch (err) {
      setCreateErr((err as Error).message);
    }
  };

  const visibleStages = () =>
    (stages.data ?? []).filter((s) => !s.archived_at);

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <div class="flex items-center justify-between">
          <h2 class="card-title text-lg">
            Stages — <span class="opacity-70 font-normal">{props.pipeline.name}</span>
          </h2>
          <button type="button" class="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
            New stage
          </button>
        </div>
        <Show when={stages.isLoading}>
          <div class="py-4 text-sm opacity-60">Loading…</div>
        </Show>
        <Show when={visibleStages().length === 0 && !stages.isLoading}>
          <div class="text-center py-6 opacity-60 text-sm">
            No stages yet. Add one to define the pipeline.
          </div>
        </Show>
        <ul class="divide-y divide-base-300 mt-2">
          <For each={visibleStages()}>
            {(s) => (
              <StageRow
                stage={s}
                onRename={async (name) => {
                  await updateStage.mutateAsync({
                    pipelineId: props.pipeline.id,
                    stageId: s.id,
                    version: s.version,
                    body: { name },
                  });
                }}
                onChangeProbability={async (probability_pct) => {
                  await updateStage.mutateAsync({
                    pipelineId: props.pipeline.id,
                    stageId: s.id,
                    version: s.version,
                    body: { probability_pct },
                  });
                }}
                onArchive={() =>
                  archiveStage.mutate({
                    pipelineId: props.pipeline.id,
                    stageId: s.id,
                    version: s.version,
                  })
                }
              />
            )}
          </For>
        </ul>
      </div>

      <Show when={showCreate()}>
        <dialog open class="modal modal-open" aria-modal="true" aria-labelledby="new-stage-title">
          <div class="modal-box">
            <h3 id="new-stage-title" class="font-bold text-lg mb-3">
              New stage
            </h3>
            <form onSubmit={handleCreateStage} class="flex flex-col gap-3">
              <input
                name="key"
                class="input input-bordered"
                placeholder="key (e.g. discovery)"
                aria-label="Stage key"
                required
                pattern="[a-z0-9][a-z0-9_-]*"
                maxLength={63}
              />
              <input
                name="name"
                class="input input-bordered"
                placeholder="Display name"
                aria-label="Stage name"
                required
              />
              <select
                name="stage_type"
                class="select select-bordered"
                aria-label="Stage type"
              >
                <option value="open">Open (in-progress)</option>
                <option value="won">Won (terminal — success)</option>
                <option value="lost">Lost (terminal — failure)</option>
              </select>
              <input
                name="probability_pct"
                type="number"
                min="0"
                max="100"
                class="input input-bordered"
                placeholder="Default probability % (0–100)"
                aria-label="Default probability"
              />
              <Show when={createErr()}>
                <div role="alert" class="alert alert-error text-sm">
                  {createErr()}
                </div>
              </Show>
              <div class="modal-action">
                <button type="button" class="btn btn-ghost" onClick={() => setShowCreate(false)}>
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" disabled={createStage.isPending}>
                  {createStage.isPending ? "Saving…" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </dialog>
      </Show>
    </div>
  );
}

function StageRow(props: {
  stage: StageRead;
  onRename: (name: string) => Promise<void>;
  onChangeProbability: (n: number) => Promise<void>;
  onArchive: () => void;
}) {
  const [editing, setEditing] = createSignal(false);
  const [nameDraft, setNameDraft] = createSignal(props.stage.name);
  const [probDraft, setProbDraft] = createSignal(String(props.stage.probability_pct));
  const [busy, setBusy] = createSignal(false);
  const [err, setErr] = createSignal<string | null>(null);

  const startEdit = () => {
    setNameDraft(props.stage.name);
    setProbDraft(String(props.stage.probability_pct));
    setErr(null);
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const tasks: Promise<void>[] = [];
      if (nameDraft().trim() && nameDraft() !== props.stage.name) {
        tasks.push(props.onRename(nameDraft().trim()));
      }
      const probNum = Number(probDraft());
      if (
        !Number.isNaN(probNum) &&
        probNum >= 0 &&
        probNum <= 100 &&
        probNum !== props.stage.probability_pct
      ) {
        tasks.push(props.onChangeProbability(probNum));
      }
      for (const t of tasks) await t;
      setEditing(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const typeBadge =
    props.stage.stage_type === "won"
      ? "badge-success"
      : props.stage.stage_type === "lost"
        ? "badge-error"
        : "badge-outline";

  return (
    <li class="py-2 flex items-center gap-2">
      <Show
        when={!editing()}
        fallback={
          <div class="flex flex-wrap gap-2 items-center flex-1">
            <input
              class="input input-bordered input-sm flex-1 min-w-[12rem]"
              aria-label="Stage name"
              value={nameDraft()}
              onInput={(e) => setNameDraft(e.currentTarget.value)}
            />
            <input
              type="number"
              min="0"
              max="100"
              class="input input-bordered input-sm w-24"
              aria-label="Probability percent"
              value={probDraft()}
              onInput={(e) => setProbDraft(e.currentTarget.value)}
            />
            <Show when={err()}>
              <span class="text-error text-xs">{err()}</span>
            </Show>
            <div class="flex gap-1 ml-auto">
              <button
                type="button"
                class="btn btn-ghost btn-xs"
                onClick={() => setEditing(false)}
                disabled={busy()}
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-primary btn-xs"
                onClick={save}
                disabled={busy()}
              >
                {busy() ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        }
      >
        <div class="flex-1 min-w-0">
          <div class="font-medium flex items-center gap-2 flex-wrap">
            <span class="badge badge-ghost badge-xs">#{props.stage.order_index}</span>
            {props.stage.name}
            <span class={`badge badge-xs ${typeBadge}`}>{props.stage.stage_type}</span>
            <span class="text-xs opacity-60">{props.stage.probability_pct}%</span>
          </div>
          <div class="text-xs opacity-60">
            key: <code>{props.stage.key}</code>
          </div>
        </div>
        <div class="flex gap-1">
          <button type="button" class="btn btn-ghost btn-xs" onClick={startEdit}>
            Edit
          </button>
          <button
            type="button"
            class="btn btn-ghost btn-xs text-error"
            onClick={props.onArchive}
            aria-label={`Archive stage ${props.stage.name}`}
          >
            Archive
          </button>
        </div>
      </Show>
    </li>
  );
}
